import { useMemo } from 'react';

import { filterMenuByRoles, transformMenuItems } from '@/config/navigation/transformer';
import type { CodeString } from '@/consts/codes';
import type { MenuItem } from '@/consts/navigation';
import { buildDynamicSection } from '@/features/navigation/utils/buildDynamicSection';
import { selectUser, useAuthStore } from '@/stores/useAuthStore';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { useRecentsStore } from '@/stores/useRecentsStore';

import { useMenuConfig } from './useMenuConfig';

export interface DynamicMenuResult {
  menu: MenuItem[];
  allMenuItems: MenuItem[];
  addRecent: (code: CodeString | undefined) => Promise<void>;
}

export const useDynamicMenu = (): DynamicMenuResult => {
  const user = useAuthStore(selectUser);
  const { menuItems } = useMenuConfig();

  const userRoles = useMemo(() => {
    if (!user?.Roles) return [];
    return user.Roles.map((role) => role.Id);
  }, [user?.Roles]);

  // Filter by roles, transform to rendered MenuItem[]
  const fullMenu = useMemo(() => {
    const filtered = filterMenuByRoles(menuItems, userRoles);
    return transformMenuItems(filtered);
  }, [menuItems, userRoles]);

  // Flatten all items with codes for recents/favorites tracking
  const allMenuItems = useMemo(() => {
    const flatten = (items: MenuItem[]): MenuItem[] =>
      items.flatMap((item) => {
        const nested = item.children ? flatten(item.children) : [];
        return item.code ? [item, ...nested] : nested;
      });
    return fullMenu.flatMap((section) => flatten(section.children ?? []));
  }, [fullMenu]);

  const { favoriteCodes, favoritesLoading } = useFavoritesStore();
  const { recentCodes, recentsLoading, addRecent } = useRecentsStore();

  const menu = useMemo(() => {
    return fullMenu.map((section) => {
      if (section.text === 'navigation.favorites.title') {
        return buildDynamicSection(
          allMenuItems,
          section,
          favoriteCodes,
          favoritesLoading,
          'navigation.favorites.children.empty'
        );
      }
      if (section.text === 'navigation.recent.title') {
        return buildDynamicSection(
          allMenuItems,
          section,
          recentCodes,
          recentsLoading,
          'navigation.recent.children.empty'
        );
      }
      return section;
    });
  }, [fullMenu, allMenuItems, favoriteCodes, favoritesLoading, recentCodes, recentsLoading]);

  return { menu, allMenuItems, addRecent };
};
